import { bugsnagNotifyWrapper } from '../containers/ErrorBoundary/utils';
import { apiSetMeshScenes } from '../api/apiScenes';
import { getMeshBotOperation } from './getMeshBotOperation';
import { pollUntilMeshBotSynced } from './pollUntilMeshBotSynced';
import { buildSyncTimeoutFallback } from './buildSyncTimeoutFallback';
import { setSceneProgress } from './setSceneProgress';

/**
 * Thunk that sends a create/update/delete request for a cloud MeshBot and then
 * polls the list until the change is reflected, keeping the UI in a loading state
 * throughout the process.
 *
 * The operation type (create/update/delete) is derived automatically from `params`
 * via {@link getMeshBotOperation}.
 *
 * Flow:
 * 1. Sets scene loading to `true`.
 * 2. Sends the API request via `apiSetMeshScenes`.
 * 3. Polls the list via {@link pollUntilMeshBotSynced} until the change appears.
 *    - If polling times out, falls back to {@link buildSyncTimeoutFallback}.
 * 4. Sets scene loading to `false` (always, via `finally`).
 *
 * @param {Object} params - The MeshBot API request payload.
 * @param {string} [params.uuid] - Present for update/delete; absent for create.
 * @param {number} [params.delete] - Set to 1 for delete operations.
 * @param {Object|undefined} [previousMeshBot] - Snapshot of the MeshBot before the operation.
 *   Required for UPDATE polling — used to detect when `last_modified` has changed.
 *   Pass `null` for CREATE and DELETE.
 * @returns {Function} Redux thunk `(dispatch) => Promise<AxiosResponse|undefined>`.
 *   Resolves with the API response on success, or `undefined` if an error occurred.
 *
 * @example
 * // Create
 * dispatch(setCloudMeshbot({ name: 'New bot', when: [], then: [] }, null));
 *
 * // Update
 * dispatch(setCloudMeshbot({ uuid: 'abc-123', name: 'Updated' }, previousMeshBot));
 *
 * // Delete
 * dispatch(setCloudMeshbot({ uuid: 'abc-123', delete: 1 }, null));
 */
export const setCloudMeshbot =
    (params, previousMeshBot = null) =>
    async (dispatch) => {
        try {
            dispatch(setSceneProgress(true));
            const response = await apiSetMeshScenes(params);
            if (response?.data?.status === 0) {
                throw new Error(response.data?.data?.error_text);
            }
            const uuid = params.uuid || response?.data?.data?.uuid;
            const operation = getMeshBotOperation(params);
            await dispatch(
                pollUntilMeshBotSynced(
                    uuid,
                    { operation, previousItem: previousMeshBot },
                    buildSyncTimeoutFallback(operation, uuid, params, previousMeshBot),
                ),
            );

            return response;
        } catch (e) {
            bugsnagNotifyWrapper(e, { params });
        } finally {
            dispatch(setSceneProgress(false));
        }
    };
